import React, { useMemo } from "react";

/**
 * 📅 PERÍODO EM MÊS/ANO — o formato que o parser de ATS entende.
 *
 * Era uma caixa de texto livre ("2021 - atual", "jan/2020 a dez/2022", "de 2019 até hoje"). O parser
 * espera MM/AAAA consistente: data por extenso, ano sozinho ou formato misturado entre um emprego e
 * outro fazem o campo de datas voltar vazio — e sem datas a triagem não calcula tempo de casa, que é
 * metade do que ela usa para inferir senioridade. A Jobscan mede 37% das rejeições por ATS vindas de
 * leitura errada de data.
 *
 * ⚠️ O QUE JÁ ESTÁ GRAVADO NÃO PODE SUMIR. 237 pessoas preencheram este campo em texto livre. Quando o
 * valor não casa com MM/AAAA, o componente NÃO limpa nem adivinha: mostra a caixa de texto original com
 * um aviso discreto, e a pessoa converte se quiser. Apagar o período de alguém para "arrumar o formato"
 * seria estragar o currículo dela em nome da nossa régua.
 *
 * Escrito em 25/09/2026, junto da correção do PDF (ver CurriculoPdfGenerator).
 */

const MESES = [
  ["01", "jan"], ["02", "fev"], ["03", "mar"], ["04", "abr"], ["05", "mai"], ["06", "jun"],
  ["07", "jul"], ["08", "ago"], ["09", "set"], ["10", "out"], ["11", "nov"], ["12", "dez"],
];

// Ano atual + 1 (quem está estudando põe a previsão de conclusão) até 50 anos atrás.
const ANOS = (() => {
  const fim = new Date().getFullYear() + 1;
  return Array.from({ length: 52 }, (_, i) => String(fim - i));
})();

/** "03/2021 - atual" -> {mi:"03", ai:"2021", mf:"", af:"", atual:true}. null se não reconhecer. */
export function lerPeriodo(bruto) {
  const t = (bruto || "").trim();
  if (!t) return { mi: "", ai: "", mf: "", af: "", atual: false };

  const parte = /(\d{2})\/(\d{4})/g;
  const achados = [...t.matchAll(parte)];
  const ehAtual = /\b(atual|hoje|presente|atualmente)\b/i.test(t);

  // Só reconhece o que está inteiramente em MM/AAAA: um "2021" solto no meio significa que a pessoa
  // escreveu de outro jeito, e aí o texto dela manda.
  const semDatas = t.replace(parte, "").replace(/\b(atual|hoje|presente|atualmente|a|até|de|desde)\b/gi, "")
                    .replace(/[\s\-–—/]/g, "");
  if (semDatas.length > 0 || achados.length === 0) return null;

  const [i, f] = achados;
  if (achados.length === 1) {
    return ehAtual
      ? { mi: i[1], ai: i[2], mf: "", af: "", atual: true }
      : null;                         // uma data só e sem "atual": não dá para saber se é início ou fim
  }
  return { mi: i[1], ai: i[2], mf: f[1], af: f[2], atual: ehAtual };
}

/** {mi,ai,mf,af,atual} -> "03/2021 - 12/2024" | "03/2021 - atual" | "" */
export function escreverPeriodo(v) {
  const ini = v.mi && v.ai ? `${v.mi}/${v.ai}` : "";
  if (!ini) return "";
  if (v.atual) return `${ini} - atual`;
  const fim = v.mf && v.af ? `${v.mf}/${v.af}` : "";
  return fim ? `${ini} - ${fim}` : ini;
}

export default function CampoPeriodo({ valor, onChange, comAtual = true, rotulo = "Período" }) {
  const lido = useMemo(() => lerPeriodo(valor), [valor]);
  const livre = lido === null;

  // Texto que o componente não reconheceu: mantém como estava, com a saída para converter.
  if (livre) {
    return (
      <div>
        <input
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          aria-label={rotulo}
          placeholder="Período"
          value={valor || ""}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={() => onChange("")}
          className="mt-1 text-[11px] text-amber-700 underline hover:text-amber-900"
          title="Sistemas de triagem leem melhor o período no formato mês/ano"
        >
          usar mês/ano (lido melhor pelos sistemas de triagem)
        </button>
      </div>
    );
  }

  const set = (campo, v) => onChange(escreverPeriodo({ ...lido, [campo]: v }));
  const sel = "rounded-lg border border-gray-300 px-2 py-2 text-sm bg-white";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        <select className={sel} aria-label={`${rotulo}: mês de início`} value={lido.mi} onChange={(e) => set("mi", e.target.value)}>
          <option value="">mês</option>
          {MESES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
        <select className={sel} aria-label={`${rotulo}: ano de início`} value={lido.ai} onChange={(e) => set("ai", e.target.value)}>
          <option value="">ano</option>
          {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <span className="text-xs text-gray-400">até</span>

        {lido.atual ? (
          <span className="text-sm font-semibold text-gray-600 px-1">hoje</span>
        ) : (
          <>
            <select className={sel} aria-label={`${rotulo}: mês de saída`} value={lido.mf} onChange={(e) => set("mf", e.target.value)}>
              <option value="">mês</option>
              {MESES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
            <select className={sel} aria-label={`${rotulo}: ano de saída`} value={lido.af} onChange={(e) => set("af", e.target.value)}>
              <option value="">ano</option>
              {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </>
        )}
      </div>

      {comAtual && (
        <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={lido.atual}
            onChange={(e) => onChange(escreverPeriodo({ ...lido, atual: e.target.checked, mf: "", af: "" }))}
          />
          estou aqui até hoje
        </label>
      )}
    </div>
  );
}
